import type { PropsWithChildren } from 'react';
import { useEffect } from 'react';
import type { Path, PathValue } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';

interface Props<T extends object> extends PropsWithChildren {
  field: Path<T>;
  condition: (
    formValue: <TPath extends Path<T>>(field: TPath) => PathValue<T, TPath>,
  ) => boolean;
  keepDataCondition?: (
    formValue: <TPath extends Path<T>>(field: TPath) => PathValue<T, TPath>,
  ) => boolean;
}

export const ConditionalField = <T extends object>({
  condition,
  field,
  children,
  keepDataCondition,
}: Props<T>) => {
  const { watch, resetField } = useFormContext<T>();
  const shouldUse = condition((f) => watch(f));
  const shouldKeepData = keepDataCondition
    ? keepDataCondition((f) => watch(f))
    : shouldUse;

  useEffect(() => {
    if (!shouldKeepData) {
      resetField(field);
    }
  }, [shouldUse, field, resetField]);

  return shouldUse && children;
};
